import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaArrowRight, FaCalendarAlt, FaPen } from "react-icons/fa";
import ErrorPage from "../../../components/ErrorPage";
import Spinner from "../../../components/Spinner";
import { useCan } from "../../../authz/useCan";
import useFormatCurrency from "../../../utils/useFormatCurrency";
import { formatDate } from "../../../utils/dateUtils";
import { translateClassName } from "../../../utils/classroomUtils";
import { loadClassroomList, loadSchoolYearDetails } from "../cache";
import { ActionButton, AvatarBlock, BackButton, EmptyState, LdsPage, ListSection, StatusBadge, cardEntryClass, listRowClass } from "../components/Lds";
import type { AppError, Classroom, SchoolYear } from "../types";
import { classroomChildren, entityKey, getYearNumber, httpStatus, idEquals } from "../utils";

const SchoolYearDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const formatCurrency = useFormatCurrency();

  const canListYears = useCan("school.years", "list");
  const canViewYearsGrant = useCan("school.years", "view");
  const canReadYears = useCan("school.years", "read");
  const canCrudYears = useCan("school.years", "crud");
  const canManageYears = useCan("school.years", "manage");
  const canViewYears = canListYears || canViewYearsGrant || canReadYears || canCrudYears || canManageYears;
  const canEditYears = canCrudYears || canManageYears;

  const [schoolYear, setSchoolYear] = useState<SchoolYear | null>(null);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AppError | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!id || !canViewYears) {
        setError({ message: t("error.403.message", { defaultValue: "You do not have permission." }), status: 403 });
        setLoading(false);
        return;
      }
      try {
        const [yearData, classroomData] = await Promise.all([loadSchoolYearDetails(id), loadClassroomList()]);
        if (!cancelled) {
          setSchoolYear(yearData);
          setClassrooms(classroomData.filter((classroom) => idEquals(classroom.schoolYear?.id, id)));
        }
      } catch (err) {
        if (!cancelled) {
          setError({
            message:
              httpStatus(err) === 404
                ? t("years.details.not_found", { defaultValue: "School year not found." })
                : t("years.details.load_failed", { defaultValue: "Failed to fetch school year details." }),
            status: httpStatus(err),
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [canViewYears, id, t]);

  const totalChildren = useMemo(() => classrooms.reduce((sum, classroom) => sum + classroomChildren(classroom).length, 0), [classrooms]);

  if (loading) return <Spinner />;
  if (error) return <ErrorPage message={error.message} status={error.status} onRetry={() => window.location.reload()} />;
  if (!schoolYear) return null;

  const startYear = getYearNumber(schoolYear.startDate);
  const endYear = getYearNumber(schoolYear.endDate);
  const formattedFee = schoolYear.defaultMonthlyFee != null ? formatCurrency(schoolYear.defaultMonthlyFee) : "—";
  const openClassroomDetails = (classroomId: string | number) =>
    navigate(`/school/classrooms/${classroomId}/details`, {
      state: { returnTo: `/school/years/${id}/details` },
    });

  return (
    <LdsPage>
      <div className="space-y-5">
        <section className="flex min-h-[88px] items-center justify-between gap-4 rounded-[28px] border border-slate-200/80 bg-white px-5 py-4 shadow-sm dark:border-white/10 dark:bg-slate-950 sm:px-7">
          <BackButton fallback="/school/years" />

          {canEditYears ? (
            <div className="ml-auto flex shrink-0 items-center gap-3">
              <ActionButton onClick={() => navigate(`/school/years/${id}/edit`)}>
                <span className="inline-flex items-center gap-2">
                  <FaPen aria-hidden="true" />
                  {t("years.details.edit", { defaultValue: "Edit school year" })}
                </span>
              </ActionButton>
            </div>
          ) : null}
        </section>

        <section className="relative overflow-hidden rounded-[28px] border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-sky-50 px-5 py-6 shadow-sm dark:border-white/10 dark:from-indigo-950/35 dark:via-slate-950 dark:to-sky-950/30 sm:px-7 sm:py-7">
          <div className="relative z-10 flex min-w-0 flex-col gap-5 lg:flex-row lg:items-center lg:justify-between lg:pr-[26%]">
            <div className="flex min-w-0 items-center gap-5">
              <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-indigo-200 to-sky-50 text-3xl text-indigo-700 shadow-lg dark:border-slate-900 dark:from-indigo-800 dark:to-sky-950 dark:text-indigo-100 sm:h-24 sm:w-24">
                <FaCalendarAlt aria-hidden="true" />
              </div>

              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                  {t("years.details.title", { defaultValue: "School year details" })}
                </p>
                <h1 className="mt-1 truncate text-3xl font-bold tracking-tight text-slate-950 dark:text-white">
                  {startYear ?? "—"} - {endYear ?? "—"}
                </h1>
                <p className="mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-300">
                  {t("years.details.date_range", { defaultValue: "Date range" })}: {formatDate(schoolYear.startDate)} - {formatDate(schoolYear.endDate)}
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <StatusBadge tone="info">{t("years.details.default_fee", { defaultValue: "Default fee" })}: {formattedFee}</StatusBadge>
                  <StatusBadge tone="success">{t("years.details.assigned_classrooms", { defaultValue: "Assigned classrooms" })}: {classrooms.length}</StatusBadge>
                </div>
              </div>
            </div>

          </div>

          <div className="pointer-events-none absolute inset-y-0 right-5 hidden items-center lg:flex" aria-hidden="true">
            <FaCalendarAlt className="text-[110px] text-indigo-200/70 dark:text-indigo-800/40" />
          </div>
        </section>

        <section className={`${cardEntryClass} grid gap-4 md:grid-cols-3`}>
          <div className="rounded-2xl border border-indigo-100 bg-indigo-50/60 p-4 dark:border-indigo-400/20 dark:bg-indigo-400/10">
            <p className="text-xs font-semibold uppercase text-slate-500">{t("years.details.range", { defaultValue: "School year" })}</p>
            <p className="mt-2 text-2xl font-semibold text-indigo-800 dark:text-indigo-100">{startYear ?? "—"} - {endYear ?? "—"}</p>
          </div>
          <div className="rounded-2xl border border-amber-100 bg-amber-50/60 p-4 dark:border-amber-400/20 dark:bg-amber-400/10">
            <p className="text-xs font-semibold uppercase text-slate-500">{t("years.details.default_fee", { defaultValue: "Default fee" })}</p>
            <p className="mt-2 text-2xl font-semibold text-amber-800 dark:text-amber-100">{formattedFee}</p>
          </div>
          <div className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4 dark:border-emerald-400/20 dark:bg-emerald-400/10">
            <p className="text-xs font-semibold uppercase text-slate-500">{t("classrooms.details.children_assigned", { defaultValue: "Children assigned" })}</p>
            <p className="mt-2 text-2xl font-semibold text-emerald-800 dark:text-emerald-100">{totalChildren}</p>
          </div>
        </section>

        <ListSection title={t("years.details.assigned_classrooms", { defaultValue: "Assigned classrooms" })} count={classrooms.length}>
          {classrooms.length > 0 ? (
            classrooms.map((room) => {
              const roomName = translateClassName(room);
              return (
                <article
                  key={entityKey(room.id)}
                  onClick={() => openClassroomDetails(room.id)}
                  className={`${listRowClass} lg:grid-cols-[minmax(260px,1fr)_minmax(180px,0.7fr)_160px_auto]`}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <AvatarBlock tone="sky">{roomName.slice(0, 2)}</AvatarBlock>
                    <div className="min-w-0">
                      <h3 className="truncate text-base font-semibold text-slate-950 dark:text-white">{roomName}</h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{t("classrooms.overview.row_subtitle", { defaultValue: "Classroom record" })}</p>
                    </div>
                  </div>
                  <StatusBadge tone="neutral">{room.classType === "KINDERGARTEN" ? t("classrooms.kindergarten.label", { defaultValue: "Kindergarten" }) : `${t("classrooms.grade.label", { defaultValue: "Grade" })} ${room.grade ?? "—"}`}</StatusBadge>
                  <StatusBadge tone="success">{t("classrooms.details.capacity", { defaultValue: "Capacity" })}: {room.maxChildren ?? "—"}</StatusBadge>
                  <div className="flex justify-start lg:justify-end">
                    <ActionButton tone="secondary" onClick={() => openClassroomDetails(room.id)}>
                      <span className="inline-flex items-center gap-2">{t("common.actions.view", { defaultValue: "View" })}<FaArrowRight aria-hidden="true" /></span>
                    </ActionButton>
                  </div>
                </article>
              );
            })
          ) : (
            <div className="p-5"><EmptyState>{t("years.details.none_assigned", { defaultValue: "No classrooms assigned." })}</EmptyState></div>
          )}
        </ListSection>
      </div>
    </LdsPage>
  );
};

export default SchoolYearDetails;
